import React, { useState } from "react";
import { Box, Button, Dialog, IconButton, TextField, Typography } from "@mui/material";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import api from "../../api/axiosInstance";

const ACCENT = "#E84610";
const DARK = "#483C3B";
const TEXT_SECONDARY = "rgba(0, 0, 0, 0.6)";
const BUTTON_SX = {
  height: 42,
  px: "22px",
  py: "8px",
  borderRadius: "8px",
  fontSize: 15,
  lineHeight: "26px",
  fontWeight: 500,
  letterSpacing: "0.46px",
  textTransform: "uppercase",
  boxSizing: "border-box",
};

// Same rule as the server (server/models/client.js): http(s) with a dotted host.
export const isValidWebsite = (value) => {
  try {
    const url = new URL(value);
    return (url.protocol === "http:" || url.protocol === "https:") && url.hostname.includes(".");
  } catch {
    return false;
  }
};

const AddCustomerModal = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [nameError, setNameError] = useState("");
  const [websiteError, setWebsiteError] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setName("");
    setWebsite("");
    setNameError("");
    setWebsiteError("");
    setFormError("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const canSubmit = name.trim() !== "" && website.trim() !== "" && !saving;

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    const trimmedWebsite = website.trim();
    if (!isValidWebsite(trimmedWebsite)) {
      setWebsiteError("Enter a website like https://example.com");
      return;
    }
    setSaving(true);
    setNameError("");
    setFormError("");
    try {
      const { data } = await api.post("/clients", { name: trimmedName, website: trimmedWebsite });
      reset();
      onCreated?.(data);
    } catch (err) {
      if (err.response?.status === 409) {
        setNameError("Customer already exists");
      } else {
        setFormError(err.response?.data?.message || err.response?.data?.error || "Could not create the customer");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: 575,
          maxWidth: "calc(100% - 32px)",
          m: 2,
          p: 3,
          borderRadius: "8px",
          boxShadow: "0px 1px 2px 0px rgba(0,0,0,0.3), 0px 2px 6px 2px rgba(0,0,0,0.15)",
          display: "flex",
          flexDirection: "column",
          gap: 4,
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: 30 }}>
          <Typography component="h2" sx={{ fontSize: 18, lineHeight: "30px", fontWeight: 700 }}>
            Add New Customer
          </Typography>
          <IconButton edge="end" size="small" aria-label="Close dialog" onClick={handleClose} sx={{ color: DARK }}>
            <HighlightOffOutlinedIcon />
          </IconButton>
        </Box>
        <Typography sx={{ fontSize: 16, lineHeight: "24px", fontWeight: 300, color: TEXT_SECONDARY }}>
          Register a new customer in the system
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <TextField
          id="add-customer-name"
          label="Company Name"
          required
          placeholder="Eg. Tech Solutions AB"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setNameError("");
          }}
          error={Boolean(nameError)}
          helperText={nameError || undefined}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          id="add-customer-website"
          label="Website Address"
          required
          placeholder="https://example.com"
          value={website}
          onChange={(e) => {
            setWebsite(e.target.value);
            setWebsiteError("");
          }}
          error={Boolean(websiteError)}
          helperText={websiteError || undefined}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        {formError && (
          <Typography role="alert" sx={{ fontSize: 14, color: "error.main" }}>
            {formError}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{
            ...BUTTON_SX,
            color: DARK,
            borderColor: DARK,
            backgroundColor: "#FFFFFF",
            "&:hover": { borderColor: DARK, backgroundColor: "#FFFFFF" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{
            ...BUTTON_SX,
            backgroundColor: ACCENT,
            "&:hover": { backgroundColor: "#C93B0A" },
            "&.Mui-disabled": { backgroundColor: "#E8A090", color: "#FFFFFF" },
          }}
        >
          Create Customer
        </Button>
      </Box>
    </Dialog>
  );
};

export default AddCustomerModal;
